import { useMemo, useState } from 'react';
import type { PourCard } from '@/lib/apiClient';
import { Button } from '@/components';
import { POUR_TYPE_LABEL, pourStatus, pourStatusLabel, chipStyle, fmtDt, fmtWallD } from './pourShared';
import {
  DEFAULT_STATUSES, DEFAULT_TYPES, POUR_STATUSES, POUR_TYPES, filterPourLog, hiddenActuals, pourLogCsv, pourLogTotals, sortPourLog,
  type PourLogSort, type PourLogSortKey,
} from './pourLog';
import { downloadCsv } from '@/lib/downloadCsv';

/**
 * The Pour Log — every pour card (main pours, will-calls, mud seals) as one
 * flat table anyone can filter, sort, total and export, for reconciling the
 * yards on a job. Read-only on purpose: the office actions stay on the work
 * queue, so this view can't be used to change an order by accident. Rows
 * still open the full pour card drawer.
 *
 * Shared by the company-wide QA/QC page (with a Job column) and a job's QA/QC
 * tab (without). The logic is in ./pourLog, where it's tested.
 */

/** Exported for the Rock Log, which renders it too: a class is only styled
 *  while a component declaring it is mounted, and the Rock Log is shown
 *  without this table. */
export const logTableCss = `
  .pl-bar { display: flex; flex-wrap: wrap; gap: 10px 16px; align-items: flex-end; padding: 12px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); }
  .pl-group { display: flex; flex-direction: column; gap: 4px; }
  .pl-lbl { font: var(--ew-fw-semibold) 10.5px/1 var(--ew-font); letter-spacing: .05em; text-transform: uppercase; color: var(--ew-n-500); }
  .pl-toggles { display: flex; flex-wrap: wrap; gap: 4px; }
  .pl-tog {
    border: 1px solid var(--ew-n-200); background: #fff; color: var(--ew-n-500); cursor: pointer;
    border-radius: var(--ew-r-pill); padding: 4px 10px; font: var(--ew-fw-medium) 12px/1.2 var(--ew-font);
  }
  .pl-tog[aria-pressed="true"] { background: var(--ew-dark); border-color: var(--ew-dark); color: #fff; }
  .pl-input {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 5px 8px;
    font: var(--ew-fw-regular) 12px var(--ew-font); background: #fff; color: var(--ew-dark);
  }
  .pl-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .pl-table th {
    text-align: left; padding: 10px var(--ew-s-3); white-space: nowrap;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); border-bottom: 1px solid var(--ew-n-100);
  }
  .pl-table th button { all: unset; cursor: pointer; }
  .pl-table th button:focus-visible { outline: 2px solid var(--ew-green); outline-offset: 2px; }
  .pl-table td { padding: 9px var(--ew-s-3); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
  .pl-table .pl-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .pl-table tbody tr { cursor: pointer; }
  .pl-table tbody tr:hover td { background: var(--ew-n-50, #FAFAFA); }
  .pl-table tfoot td { font-weight: 600; border-top: 2px solid var(--ew-n-200); border-bottom: 0; background: var(--ew-n-50, #FAFAFA); }
  .pl-muted { color: var(--ew-n-500); }
`;

/** Filter-pill toggle, shared with the Rock Log. */
export function toggle(list: readonly string[], v: string): string[] {
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
}

/** Save a log's CSV as `<fileName>-YYYY-MM-DD.csv`, shared with the Rock Log. Lives in lib/downloadCsv now; re-exported so those importers are untouched. */
export { downloadCsv };

const fmtCy = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });

export default function PourLogTable({ cards, showJob, fileName, onOpen }: {
  cards: readonly PourCard[];
  showJob: boolean;
  /** Without the extension, e.g. "pour-log-10952". */
  fileName: string;
  onOpen: (id: number) => void;
}) {
  const [search, setSearch] = useState('');
  const [types, setTypes] = useState<readonly string[]>(DEFAULT_TYPES);
  const [statuses, setStatuses] = useState<readonly string[]>(DEFAULT_STATUSES);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sort, setSort] = useState<PourLogSort>({ key: 'pourDate', dir: 1 });

  const rows = useMemo(
    () => sortPourLog(filterPourLog(cards, { search, types, statuses, from, to }), sort),
    [cards, search, types, statuses, from, to, sort],
  );
  const totals = useMemo(() => pourLogTotals(rows), [rows]);
  const hidden = useMemo(
    () => hiddenActuals(cards, { search, types, statuses, from, to }),
    [cards, search, types, statuses, from, to],
  );
  const isDefault = search === '' && from === '' && to === ''
    && types.length === DEFAULT_TYPES.length && DEFAULT_TYPES.every((t) => types.includes(t))
    && statuses.length === DEFAULT_STATUSES.length && DEFAULT_STATUSES.every((s) => statuses.includes(s));
  const reset = () => {
    setSearch(''); setTypes(DEFAULT_TYPES); setStatuses(DEFAULT_STATUSES); setFrom(''); setTo('');
  };

  const exportCsv = () => downloadCsv(pourLogCsv(rows), fileName);

  const th = (key: PourLogSortKey, label: string, opts: { num?: boolean; title?: string } = {}) => {
    const active = sort.key === key;
    return (
      <th className={opts.num ? 'pl-num' : undefined} title={opts.title}
          aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
        <button type="button"
                onClick={() => setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }))}>
          {label}{active ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
        </button>
      </th>
    );
  };
  const colCount = showJob ? 11 : 10;

  return (
    <div>
      <style>{logTableCss}</style>

      {/* ── Filters ── */}
      <div className="pl-bar">
        <div className="pl-group">
          <span className="pl-lbl">Search</span>
          <input className="pl-input" style={{ width: 210 }} value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder={showJob ? 'Job, element, mix, vendor…' : 'Element, location, mix, vendor…'} aria-label="Search pours" />
        </div>
        <div className="pl-group">
          <span className="pl-lbl">Pour type</span>
          <div className="pl-toggles">
            {POUR_TYPES.map((t) => (
              <button key={t} type="button" className="pl-tog" aria-pressed={types.includes(t)}
                      onClick={() => setTypes((l) => toggle(l, t))}>
                {POUR_TYPE_LABEL[t]}
              </button>
            ))}
          </div>
        </div>
        <div className="pl-group">
          <span className="pl-lbl">Status</span>
          <div className="pl-toggles">
            {POUR_STATUSES.map((s) => (
              <button key={s} type="button" className="pl-tog" aria-pressed={statuses.includes(s)}
                      onClick={() => setStatuses((l) => toggle(l, s))}>
                {pourStatus(s).label}
              </button>
            ))}
          </div>
        </div>
        <div className="pl-group">
          <span className="pl-lbl">Pour date</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input className="pl-input" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} aria-label="From date" />
            <span className="pl-muted" style={{ fontSize: 12 }}>to</span>
            <input className="pl-input" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} aria-label="To date" />
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {!isDefault && (
            <button type="button" className="ew-btn-link" style={{ color: 'var(--ew-n-500)', font: 'var(--ew-fw-medium) 12px var(--ew-font)' }} onClick={reset}>
              Reset filters
            </button>
          )}
          <Button variant="secondary" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
            Export CSV ({rows.length})
          </Button>
        </div>
      </div>

      {/* Reported yards the filters are hiding: loud, because a reconciliation
          quoted without them is wrong (see hiddenActuals). */}
      {hidden.rows > 0 && (
        <div role="status" style={{ margin: '10px var(--ew-s-4) 0', padding: '8px 11px', background: 'var(--ew-safety-50)', border: '1px solid var(--ew-safety-100)', borderRadius: 'var(--ew-r-md)', font: 'var(--ew-fw-regular) 12px/1.5 var(--ew-font)', color: 'var(--ew-safety-700)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            <b>{fmtCy(hidden.cy)} CY of Final Quantity Report yards</b> {hidden.rows === 1 ? 'is' : 'are'} on {hidden.rows} row{hidden.rows === 1 ? '' : 's'} hidden
            by the pour type or status filter (usually a report that landed on a will-call), so {hidden.rows === 1 ? 'it isn’t' : 'they aren’t'} in the Actual total.
          </span>
          <button type="button" className="ew-btn-link" style={{ color: 'var(--ew-safety-700)', font: 'var(--ew-fw-semibold) 12px var(--ew-font)', textDecoration: 'underline' }}
                  onClick={() => { setTypes(POUR_TYPES); setStatuses(POUR_STATUSES); }}>
            Show all types &amp; statuses
          </button>
        </div>
      )}

      {/* ── Table ── */}
      {rows.length === 0 ? (
        <div style={{ padding: 'var(--ew-s-5)', color: 'var(--ew-n-500)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
          No pours match these filters.
        </div>
      ) : (
        <div className="ew-table-scroll">
          <table className="pl-table">
            <thead>
              <tr>
                {showJob && th('job', 'Job')}
                {th('pourDate', 'Pour Date')}
                {th('type', 'Type')}
                {th('element', 'Element')}
                {th('mix', 'Mix ID')}
                {th('orderedCy', 'Ordered CY', { num: true, title: 'The number the field wrote on the order ("80 plus" counts as 80)' })}
                {th('actualCy', 'Actual CY', { num: true, title: "From the job's Final Quantity Report, when one came in" })}
                {th('actualDate', 'Actual Date', { title: "The pour date on the job's Final Quantity Report" })}
                {th('vendor', 'Vendor')}
                {th('status', 'Status')}
                {th('source', 'Source')}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const st = pourStatus(c.status);
                const cs = chipStyle(c.status, c.pourType);
                const unreadable = c.qtyCy == null && !!c.qtyText?.trim();
                return (
                  <tr key={c.id} onClick={() => onOpen(c.id)}>
                    {showJob && (
                      <td>
                        <div style={{ fontWeight: 600 }}>{c.project?.jobNumber ?? c.jobNumberRaw ?? '—'}</div>
                        {c.project && <div className="pl-muted" style={{ fontSize: 12, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.project.title}</div>}
                      </td>
                    )}
                    <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDt(c.pourDatetime)}</td>
                    <td>
                      <span style={{ display: 'inline-block', borderRadius: 'var(--ew-r-pill)', padding: '2px 9px', font: 'var(--ew-fw-semibold) 11px/1.5 var(--ew-font)', whiteSpace: 'nowrap', background: cs.bg, border: cs.border, color: cs.fg, textDecoration: cs.strike ? 'line-through' : undefined }}>
                        {POUR_TYPE_LABEL[c.pourType] ?? c.pourType}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{c.element ?? '—'}</div>
                      {c.location && <div className="pl-muted" style={{ fontSize: 12 }}>{c.location}</div>}
                    </td>
                    <td className="pl-muted">{c.mix ?? '—'}</td>
                    <td className="pl-num">
                      {c.qtyCy != null ? fmtCy(c.qtyCy) : unreadable
                        ? <span style={{ color: 'var(--ew-safety-700)' }} title="Not a number, so it isn't in the Ordered total">“{c.qtyText}”</span>
                        : <span className="pl-muted">—</span>}
                    </td>
                    <td className="pl-num">{c.actualQtyCy != null ? fmtCy(c.actualQtyCy) : <span className="pl-muted">—</span>}</td>
                    <td className="pl-muted" style={{ whiteSpace: 'nowrap' }}>{c.actualPourAt ? fmtWallD(c.actualPourAt) : '—'}</td>
                    <td className="pl-muted">{c.vendor ?? '—'}</td>
                    <td style={{ whiteSpace: 'nowrap', color: st.color }}>{pourStatusLabel(c.status, c.pourType)}</td>
                    <td className="pl-muted" style={{ whiteSpace: 'nowrap' }}>{c.source === 'manual' ? 'Office entry' : 'Field form'}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={showJob ? 5 : 4}>Total · {totals.rows} pour{totals.rows === 1 ? '' : 's'}</td>
                <td className="pl-num">{fmtCy(totals.orderedCy)}</td>
                <td className="pl-num">{fmtCy(totals.actualCy)}</td>
                <td colSpan={colCount - (showJob ? 7 : 6)} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* What the totals do and don't include — so nobody quotes a short number. */}
      <div style={{ padding: '10px var(--ew-s-4)', borderTop: '1px solid var(--ew-n-100)', font: 'var(--ew-fw-regular) 12px/1.5 var(--ew-font)', color: 'var(--ew-n-500)' }}>
        Totals cover the rows shown. <b>Ordered CY</b> is what was written on the order; <b>Actual CY</b> comes from Final Quantity Reports.
        {totals.noActual > 0 && <> {totals.noActual} of these {totals.rows} have no Final Quantity Report, so they aren't in the Actual total.</>}
        {totals.unreadableQty > 0 && <> <span style={{ color: 'var(--ew-safety-700)' }}>{totals.unreadableQty} have a quantity that isn't a number and aren't in the Ordered total.</span></>}
        {types.includes('willcall')
          ? <> <span style={{ color: 'var(--ew-safety-700)' }}>Will-calls are included — they're placeholders, not pours, so their yards shouldn't be counted toward what was poured.</span></>
          : <> Will-calls are left out by default: nothing is poured from a will-call.</>}      </div>
    </div>
  );
}
